import * as z from 'zod';

export const QueryModelDataRequest = z.object({
  modelId: z
    .string()
    .describe(
      'ID or slug of the model to query. Call `List Models` to retrieve available model IDs.'
    )
    .max(5120),
  options: z.object({
    interval: z
      .object({
        start: z
          .date()
          .describe('Period start date in UTC.')
          .optional()
          .nullable(),
        end: z.date().describe('Period end date in UTC.').optional().nullable(),
        timezone: z
          .string()
          .describe('Timezone')
          .max(1024)
          .optional()
          .nullable(),
      })
      .describe('Time interval for the data query.'),
    fields: z.array(z.string()).max(60),
    sort: z
      .object({
        fieldName: z.string().describe('Order by field').max(5120).optional(),
        order: z.enum(['ASC', 'DESC']).optional(),
        nullsLast: z
          .boolean()
          .describe(
            'Place NULL values last in the result set (relevant for DESC order)'
          )
          .optional()
          .nullable(),
      })
      .describe(
        'Sort order for the results. Supports sorting by a single field only.'
      )
      .optional(),
    filters: z
      .array(
        z.object({
          field: z
            .string()
            .describe(
              "Name of the field to filter by. Must match a field name from the model's measures or dimensions."
            )
            .max(5120)
            .optional(),
          values: z.array(z.string()).max(100).optional(),
          prefix: z.enum(['IS', 'NOT']).optional(),
          condition: z
            .enum([
              'EQUAL',
              'GREATER_THAN',
              'GREATER_THAN_OR_EQUAL',
              'LESS_THAN',
              'LESS_THAN_OR_EQUAL',
              'NULL',
              'EMPTY',
              'START_WITH',
              'END_WITH',
              'CONTAINS_ANY',
              'RANGE_II',
              'RANGE_IE',
              'RANGE_EI',
              'RANGE_EE',
              'CONTAINS_ALL',
            ])
            .optional(),
        })
      )
      .max(60)
      .optional(),
    paging: z
      .object({
        limit: z
          .number()
          .int()
          .describe('Number of items to load.')
          .min(0)
          .max(5000)
          .optional()
          .nullable(),
        offset: z
          .number()
          .int()
          .describe('Number of items to skip in the current sort order.')
          .min(0)
          .optional()
          .nullable(),
      })
      .describe('Pagination options.')
      .optional(),
    formattingEnabled: z
      .boolean()
      .describe(
        'Whether to format numeric values as human-readable strings. For example, `1500` displays as `$1,500.00` or `1.5K` when enabled.\n\nDefault: `false`'
      )
      .optional()
      .nullable(),
    cacheControl: z
      .object({
        cacheControl: z
          .enum(['ALLOW_STALE', 'UPDATE_AFTER', 'NO_STALE', 'NO_CACHE'])
          .optional(),
        maxAge: z
          .number()
          .int()
          .describe('Max age in seconds.')
          .optional()
          .nullable(),
      })
      .describe('Cache control settings.')
      .optional(),
    totalsIncluded: z
      .boolean()
      .describe(
        'Whether to include a totals row in the response. When `true`, the `totals` field contains the sum of all numeric fields across the full (unpaginated) result set.\n\nDefault: `false`'
      )
      .optional()
      .nullable(),
  }),
});
export const QueryModelDataResponse = z.object({
  results: z
    .array(
      z.object({
        fields: z
          .record(
            z.string(),
            z.intersection(
              z.object({
                formattedValue: z
                  .string()
                  .describe(
                    'Human-readable string representation of the value. Populated when `formattingEnabled` is `true` in the request.'
                  )
                  .max(5120)
                  .optional()
                  .nullable(),
              }),
              z.xor([
                z.object({
                  numericValue: z.never().optional(),
                  timestampValue: z.never().optional(),
                  booleanValue: z.never().optional(),
                  stringValue: z.never().optional(),
                  arrayValue: z.never().optional(),
                  objectValue: z.never().optional(),
                }),
                z.object({
                  timestampValue: z.never().optional(),
                  booleanValue: z.never().optional(),
                  stringValue: z.never().optional(),
                  arrayValue: z.never().optional(),
                  objectValue: z.never().optional(),
                  numericValue: z.number().describe('Numeric value.'),
                }),
                z.object({
                  numericValue: z.never().optional(),
                  booleanValue: z.never().optional(),
                  stringValue: z.never().optional(),
                  arrayValue: z.never().optional(),
                  objectValue: z.never().optional(),
                  timestampValue: z.date().describe('Date and time value.'),
                }),
                z.object({
                  numericValue: z.never().optional(),
                  timestampValue: z.never().optional(),
                  stringValue: z.never().optional(),
                  arrayValue: z.never().optional(),
                  objectValue: z.never().optional(),
                  booleanValue: z.boolean().describe('Boolean value.'),
                }),
                z.object({
                  numericValue: z.never().optional(),
                  timestampValue: z.never().optional(),
                  booleanValue: z.never().optional(),
                  arrayValue: z.never().optional(),
                  objectValue: z.never().optional(),
                  stringValue: z.string().describe('String value.').max(5120),
                }),
                z.object({
                  numericValue: z.never().optional(),
                  timestampValue: z.never().optional(),
                  booleanValue: z.never().optional(),
                  stringValue: z.never().optional(),
                  objectValue: z.never().optional(),
                  arrayValue: z.array(z.any()).describe('Array value.'),
                }),
                z.object({
                  numericValue: z.never().optional(),
                  timestampValue: z.never().optional(),
                  booleanValue: z.never().optional(),
                  stringValue: z.never().optional(),
                  arrayValue: z.never().optional(),
                  objectValue: z
                    .record(z.string(), z.any())
                    .describe('Object value with key-value pairs.'),
                }),
              ])
            )
          )
          .describe('Map of field name to its value for this row.')
          .optional(),
      })
    )
    .max(1000)
    .optional(),
  cacheStatus: z
    .object({
      status: z
        .enum(['MISS', 'HIT', 'STALE'])
        .describe('Cache status.')
        .optional(),
      createdAt: z.date().describe('Updated at.').optional().nullable(),
      expiresAt: z.date().describe('Expires at.').optional().nullable(),
    })
    .describe('Cache status of the response.')
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
    })
    .describe('Pagination metadata.')
    .optional(),
  totals: z
    .object({
      fields: z
        .record(
          z.string(),
          z.intersection(
            z.object({
              formattedValue: z
                .string()
                .describe(
                  'Human-readable string representation of the value. Populated when `formattingEnabled` is `true` in the request.'
                )
                .max(5120)
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                numericValue: z.never().optional(),
                timestampValue: z.never().optional(),
                booleanValue: z.never().optional(),
                stringValue: z.never().optional(),
                arrayValue: z.never().optional(),
                objectValue: z.never().optional(),
              }),
              z.object({
                timestampValue: z.never().optional(),
                booleanValue: z.never().optional(),
                stringValue: z.never().optional(),
                arrayValue: z.never().optional(),
                objectValue: z.never().optional(),
                numericValue: z.number().describe('Numeric value.'),
              }),
              z.object({
                numericValue: z.never().optional(),
                booleanValue: z.never().optional(),
                stringValue: z.never().optional(),
                arrayValue: z.never().optional(),
                objectValue: z.never().optional(),
                timestampValue: z.date().describe('Date and time value.'),
              }),
              z.object({
                numericValue: z.never().optional(),
                timestampValue: z.never().optional(),
                stringValue: z.never().optional(),
                arrayValue: z.never().optional(),
                objectValue: z.never().optional(),
                booleanValue: z.boolean().describe('Boolean value.'),
              }),
              z.object({
                numericValue: z.never().optional(),
                timestampValue: z.never().optional(),
                booleanValue: z.never().optional(),
                arrayValue: z.never().optional(),
                objectValue: z.never().optional(),
                stringValue: z.string().describe('String value.').max(5120),
              }),
              z.object({
                numericValue: z.never().optional(),
                timestampValue: z.never().optional(),
                booleanValue: z.never().optional(),
                stringValue: z.never().optional(),
                objectValue: z.never().optional(),
                arrayValue: z.array(z.any()).describe('Array value.'),
              }),
              z.object({
                numericValue: z.never().optional(),
                timestampValue: z.never().optional(),
                booleanValue: z.never().optional(),
                stringValue: z.never().optional(),
                arrayValue: z.never().optional(),
                objectValue: z
                  .record(z.string(), z.any())
                  .describe('Object value with key-value pairs.'),
              }),
            ])
          )
        )
        .describe('Map of field name to its value for this row.')
        .optional(),
    })
    .describe(
      'Totals row with the sum of all numeric fields across the full result set.\n\nReturned only when `totalsIncluded` is `true`.'
    )
    .optional(),
});
export const GetModelRequest = z.object({
  modelId: z
    .string()
    .describe(
      'ID or slug of the model to retrieve. Call `List Models` to retrieve available model IDs.'
    )
    .max(5120),
});
export const GetModelResponse = z.object({
  model: z
    .object({
      _id: z.string().describe('Model ID.').max(5120).optional(),
      slug: z
        .string()
        .describe('URL-friendly identifier for the model.')
        .max(5120)
        .optional(),
      version: z.number().int().describe('Model version number.').optional(),
      measures: z
        .array(
          z.object({
            name: z
              .string()
              .describe(
                "Field name. Use this value in the `fields` array of a query request to retrieve this field's data."
              )
              .max(5120)
              .optional(),
            type: z
              .enum([
                'UNKNOWN',
                'STRING',
                'NUMBER',
                'BOOLEAN',
                'DATE',
                'DATE_TIME',
                'OBJECT',
                'ARRAY',
              ])
              .describe('Data type of the field.')
              .optional(),
            filters: z
              .object({
                prefixes: z
                  .array(z.enum(['IS', 'NOT']))
                  .max(100)
                  .optional(),
                conditions: z
                  .array(
                    z.enum([
                      'EQUAL',
                      'GREATER_THAN',
                      'GREATER_THAN_OR_EQUAL',
                      'LESS_THAN',
                      'LESS_THAN_OR_EQUAL',
                      'NULL',
                      'EMPTY',
                      'START_WITH',
                      'END_WITH',
                      'CONTAINS_ANY',
                      'RANGE_II',
                      'RANGE_IE',
                      'RANGE_EI',
                      'RANGE_EE',
                      'CONTAINS_ALL',
                    ])
                  )
                  .max(100)
                  .optional(),
              })
              .describe('Supported filter operations for this field.')
              .optional(),
            format: z
              .record(z.string(), z.any())
              .describe(
                "Formatting configuration for the field's values, compatible with the JavaScript `Intl` API."
              )
              .optional()
              .nullable(),
            groupSlug: z
              .string()
              .describe(
                'Grouping identifier. Fields with the same `groupSlug` are logically related.'
              )
              .max(5120)
              .optional()
              .nullable(),
            description: z
              .string()
              .describe(
                'Human-readable description of what this field represents.'
              )
              .max(5120)
              .optional()
              .nullable(),
            dependsOn: z.array(z.string()).max(100).optional(),
          })
        )
        .max(1000)
        .optional(),
      dimensions: z
        .array(
          z.object({
            name: z
              .string()
              .describe(
                "Field name. Use this value in the `fields` array of a query request to retrieve this field's data."
              )
              .max(5120)
              .optional(),
            type: z
              .enum([
                'UNKNOWN',
                'STRING',
                'NUMBER',
                'BOOLEAN',
                'DATE',
                'DATE_TIME',
                'OBJECT',
                'ARRAY',
              ])
              .describe('Data type of the field.')
              .optional(),
            filters: z
              .object({
                prefixes: z
                  .array(z.enum(['IS', 'NOT']))
                  .max(100)
                  .optional(),
                conditions: z
                  .array(
                    z.enum([
                      'EQUAL',
                      'GREATER_THAN',
                      'GREATER_THAN_OR_EQUAL',
                      'LESS_THAN',
                      'LESS_THAN_OR_EQUAL',
                      'NULL',
                      'EMPTY',
                      'START_WITH',
                      'END_WITH',
                      'CONTAINS_ANY',
                      'RANGE_II',
                      'RANGE_IE',
                      'RANGE_EI',
                      'RANGE_EE',
                      'CONTAINS_ALL',
                    ])
                  )
                  .max(100)
                  .optional(),
              })
              .describe('Supported filter operations for this field.')
              .optional(),
            format: z
              .record(z.string(), z.any())
              .describe(
                "Formatting configuration for the field's values, compatible with the JavaScript `Intl` API."
              )
              .optional()
              .nullable(),
            groupSlug: z
              .string()
              .describe(
                'Grouping identifier. Fields with the same `groupSlug` are logically related.'
              )
              .max(5120)
              .optional()
              .nullable(),
            description: z
              .string()
              .describe(
                'Human-readable description of what this field represents.'
              )
              .max(5120)
              .optional()
              .nullable(),
            dependsOn: z.array(z.string()).max(100).optional(),
          })
        )
        .max(1000)
        .optional(),
      parameters: z
        .array(
          z.object({
            name: z
              .string()
              .describe(
                "Field name. Use this value in the `fields` array of a query request to retrieve this field's data."
              )
              .max(5120)
              .optional(),
            type: z
              .enum([
                'UNKNOWN',
                'STRING',
                'NUMBER',
                'BOOLEAN',
                'DATE',
                'DATE_TIME',
                'OBJECT',
                'ARRAY',
              ])
              .describe('Data type of the field.')
              .optional(),
            filters: z
              .object({
                prefixes: z
                  .array(z.enum(['IS', 'NOT']))
                  .max(100)
                  .optional(),
                conditions: z
                  .array(
                    z.enum([
                      'EQUAL',
                      'GREATER_THAN',
                      'GREATER_THAN_OR_EQUAL',
                      'LESS_THAN',
                      'LESS_THAN_OR_EQUAL',
                      'NULL',
                      'EMPTY',
                      'START_WITH',
                      'END_WITH',
                      'CONTAINS_ANY',
                      'RANGE_II',
                      'RANGE_IE',
                      'RANGE_EI',
                      'RANGE_EE',
                      'CONTAINS_ALL',
                    ])
                  )
                  .max(100)
                  .optional(),
              })
              .describe('Supported filter operations for this field.')
              .optional(),
            format: z
              .record(z.string(), z.any())
              .describe(
                "Formatting configuration for the field's values, compatible with the JavaScript `Intl` API."
              )
              .optional()
              .nullable(),
            groupSlug: z
              .string()
              .describe(
                'Grouping identifier. Fields with the same `groupSlug` are logically related.'
              )
              .max(5120)
              .optional()
              .nullable(),
            description: z
              .string()
              .describe(
                'Human-readable description of what this field represents.'
              )
              .max(5120)
              .optional()
              .nullable(),
            dependsOn: z.array(z.string()).max(100).optional(),
          })
        )
        .max(1000)
        .optional(),
      description: z
        .string()
        .describe('Human-readable description of the model.')
        .max(5120)
        .optional()
        .nullable(),
      keywords: z.array(z.string()).max(100).optional(),
    })
    .describe('Retrieved semantic model.')
    .optional(),
});
export const ListModelsRequest = z.object({});
export const ListModelsResponse = z.object({
  models: z
    .array(
      z.object({
        _id: z.string().describe('Model ID.').max(5120).optional(),
        slug: z
          .string()
          .describe('URL-friendly identifier for the model.')
          .max(5120)
          .optional(),
        version: z.number().int().describe('Model version number.').optional(),
        measures: z
          .array(
            z.object({
              name: z
                .string()
                .describe(
                  "Field name. Use this value in the `fields` array of a query request to retrieve this field's data."
                )
                .max(5120)
                .optional(),
              type: z
                .enum([
                  'UNKNOWN',
                  'STRING',
                  'NUMBER',
                  'BOOLEAN',
                  'DATE',
                  'DATE_TIME',
                  'OBJECT',
                  'ARRAY',
                ])
                .describe('Data type of the field.')
                .optional(),
              filters: z
                .object({
                  prefixes: z
                    .array(z.enum(['IS', 'NOT']))
                    .max(100)
                    .optional(),
                  conditions: z
                    .array(
                      z.enum([
                        'EQUAL',
                        'GREATER_THAN',
                        'GREATER_THAN_OR_EQUAL',
                        'LESS_THAN',
                        'LESS_THAN_OR_EQUAL',
                        'NULL',
                        'EMPTY',
                        'START_WITH',
                        'END_WITH',
                        'CONTAINS_ANY',
                        'RANGE_II',
                        'RANGE_IE',
                        'RANGE_EI',
                        'RANGE_EE',
                        'CONTAINS_ALL',
                      ])
                    )
                    .max(100)
                    .optional(),
                })
                .describe('Supported filter operations for this field.')
                .optional(),
              format: z
                .record(z.string(), z.any())
                .describe(
                  "Formatting configuration for the field's values, compatible with the JavaScript `Intl` API."
                )
                .optional()
                .nullable(),
              groupSlug: z
                .string()
                .describe(
                  'Grouping identifier. Fields with the same `groupSlug` are logically related.'
                )
                .max(5120)
                .optional()
                .nullable(),
              description: z
                .string()
                .describe(
                  'Human-readable description of what this field represents.'
                )
                .max(5120)
                .optional()
                .nullable(),
              dependsOn: z.array(z.string()).max(100).optional(),
            })
          )
          .max(1000)
          .optional(),
        dimensions: z
          .array(
            z.object({
              name: z
                .string()
                .describe(
                  "Field name. Use this value in the `fields` array of a query request to retrieve this field's data."
                )
                .max(5120)
                .optional(),
              type: z
                .enum([
                  'UNKNOWN',
                  'STRING',
                  'NUMBER',
                  'BOOLEAN',
                  'DATE',
                  'DATE_TIME',
                  'OBJECT',
                  'ARRAY',
                ])
                .describe('Data type of the field.')
                .optional(),
              filters: z
                .object({
                  prefixes: z
                    .array(z.enum(['IS', 'NOT']))
                    .max(100)
                    .optional(),
                  conditions: z
                    .array(
                      z.enum([
                        'EQUAL',
                        'GREATER_THAN',
                        'GREATER_THAN_OR_EQUAL',
                        'LESS_THAN',
                        'LESS_THAN_OR_EQUAL',
                        'NULL',
                        'EMPTY',
                        'START_WITH',
                        'END_WITH',
                        'CONTAINS_ANY',
                        'RANGE_II',
                        'RANGE_IE',
                        'RANGE_EI',
                        'RANGE_EE',
                        'CONTAINS_ALL',
                      ])
                    )
                    .max(100)
                    .optional(),
                })
                .describe('Supported filter operations for this field.')
                .optional(),
              format: z
                .record(z.string(), z.any())
                .describe(
                  "Formatting configuration for the field's values, compatible with the JavaScript `Intl` API."
                )
                .optional()
                .nullable(),
              groupSlug: z
                .string()
                .describe(
                  'Grouping identifier. Fields with the same `groupSlug` are logically related.'
                )
                .max(5120)
                .optional()
                .nullable(),
              description: z
                .string()
                .describe(
                  'Human-readable description of what this field represents.'
                )
                .max(5120)
                .optional()
                .nullable(),
              dependsOn: z.array(z.string()).max(100).optional(),
            })
          )
          .max(1000)
          .optional(),
        parameters: z
          .array(
            z.object({
              name: z
                .string()
                .describe(
                  "Field name. Use this value in the `fields` array of a query request to retrieve this field's data."
                )
                .max(5120)
                .optional(),
              type: z
                .enum([
                  'UNKNOWN',
                  'STRING',
                  'NUMBER',
                  'BOOLEAN',
                  'DATE',
                  'DATE_TIME',
                  'OBJECT',
                  'ARRAY',
                ])
                .describe('Data type of the field.')
                .optional(),
              filters: z
                .object({
                  prefixes: z
                    .array(z.enum(['IS', 'NOT']))
                    .max(100)
                    .optional(),
                  conditions: z
                    .array(
                      z.enum([
                        'EQUAL',
                        'GREATER_THAN',
                        'GREATER_THAN_OR_EQUAL',
                        'LESS_THAN',
                        'LESS_THAN_OR_EQUAL',
                        'NULL',
                        'EMPTY',
                        'START_WITH',
                        'END_WITH',
                        'CONTAINS_ANY',
                        'RANGE_II',
                        'RANGE_IE',
                        'RANGE_EI',
                        'RANGE_EE',
                        'CONTAINS_ALL',
                      ])
                    )
                    .max(100)
                    .optional(),
                })
                .describe('Supported filter operations for this field.')
                .optional(),
              format: z
                .record(z.string(), z.any())
                .describe(
                  "Formatting configuration for the field's values, compatible with the JavaScript `Intl` API."
                )
                .optional()
                .nullable(),
              groupSlug: z
                .string()
                .describe(
                  'Grouping identifier. Fields with the same `groupSlug` are logically related.'
                )
                .max(5120)
                .optional()
                .nullable(),
              description: z
                .string()
                .describe(
                  'Human-readable description of what this field represents.'
                )
                .max(5120)
                .optional()
                .nullable(),
              dependsOn: z.array(z.string()).max(100).optional(),
            })
          )
          .max(1000)
          .optional(),
        description: z
          .string()
          .describe('Human-readable description of the model.')
          .max(5120)
          .optional()
          .nullable(),
        keywords: z.array(z.string()).max(100).optional(),
      })
    )
    .max(100)
    .optional(),
});
